import { FirebaseError } from '@firebase/util';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Logo from '@/assets/icons/logo.svg?react';
import { Button } from '@/components/ui/button';
import { Form, FormFieldPassword, FormFieldText, FormMessageRoot } from '@/components/ui/form';
import { Footnote, Heading3 } from '@/components/ui/typography';
import { getTenantId } from '@/lib/api/get-tenant-id';
import { isServerSideEnabled } from '@/lib/file/common';
import { auth, FIREBASE_TENANT_ID_LOCAL_STORAGE_KEY } from '@/lib/firebase/client';
import { getFirebaseContinueUrl } from '@/lib/utils';
import {
  type Credentials,
  credentialsSchema,
  type RequestResetPassword,
  requestResetPasswordSchema,
} from '@/schemas/credentials';

const maybeAddTenantIdToAuth = async (emailAddress: string) => {
  if (!isServerSideEnabled()) {
    return;
  }

  try {
    const tenantId = await getTenantId(emailAddress);
    auth.tenantId = tenantId;
    localStorage.setItem(FIREBASE_TENANT_ID_LOCAL_STORAGE_KEY, tenantId);
  } catch (err) {
    if (isServerSideEnabled()) {
      console.error(err);

      // tenantId is required to use server-side
      throw new Error();
    }
  }
};

const searchSchema = z.object({
  destination: z.string().optional(),
});

const expiredAccessMessage =
  'Je toegang tot AlexAI is verlopen. Stuur een email aan info@alexai.nl als je je toegang wilt verlengen';

const isUserDisabledError = (error: FirebaseError) =>
  error.code === 'auth/user-disabled' || error.message.includes('USER_DISABLED');

const LoginComponent = ({
  setShowRequestPasswordReset,
}: {
  setShowRequestPasswordReset: (showRequestPasswordReset: boolean) => void;
}) => {
  const search = useSearch({ from: '/(auth)/login' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const form = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      emailAddress: '',
      password: '',
    },
  });

  const onSubmit = async (credentials: { emailAddress: string; password: string }) => {
    try {
      await maybeAddTenantIdToAuth(credentials.emailAddress);
    } catch {
      return form.setError('root', {
        type: 'manual',
        message: 'Gebruiker heeft geen tenantId.',
      });
    }

    setIsSubmitting(true);

    signInWithEmailAndPassword(auth, credentials.emailAddress, credentials.password)
      .then(() => {
        const destination = search.destination;
        navigate({ to: destination || '/' });
      })
      .catch((e) => {
        if (e instanceof FirebaseError) {
          if (isUserDisabledError(e)) {
            return form.setError('root', {
              type: 'manual',
              message: expiredAccessMessage,
            });
          }

          switch (e.code) {
            case 'auth/invalid-credential':
            case 'auth/invalid-email':
            case 'auth/invalid-login-credentials':
            case 'auth/user-not-found':
            case 'auth/wrong-password':
              return form.setError('root', {
                type: 'manual',
                message: 'Uw e-mailadres of wachtwoord is onjuist.',
              });
          }
        }
        return form.setError('root', {
          type: 'manual',
          message: 'Er is een onbekende fout voorgekomen.',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onForgotPasswordClick = () => {
    setShowRequestPasswordReset(true);
  };

  return (
    <div className="flex min-h-screen flex-row items-center justify-center">
      <div className="flex w-[317px] flex-col items-start justify-center gap-6">
        <Logo className="size-10" strokeWidth="1.0" />
        <Heading3>Inloggen</Heading3>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col items-start gap-4"
          >
            <FormFieldText
              control={form.control}
              name="emailAddress"
              label="Email"
              inputStyle="rounded-input border border-input bg-background py-2 pr-3 pl-4 w-full"
            />

            <FormFieldPassword
              control={form.control}
              name="password"
              label="Wachtwoord"
              inputStyle="rounded-input border border-input bg-background py-2 pr-3 pl-4 w-full"
            />

            <FormMessageRoot />

            <div className="mt-2 flex w-full flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
              <Button className="w-full px-3 py-2" disabled={form.formState.isSubmitting}>
                {(form.formState.isSubmitting || isSubmitting) && (
                  <Loader2 className="animate-spin" />
                )}
                Inloggen
              </Button>
            </div>
          </form>
        </Form>
        <Button
          variant="link"
          className="border-none p-0 shadow-none"
          onClick={onForgotPasswordClick}
        >
          <Footnote className="text-muted-foreground underline">Wachtwoord vergeten?</Footnote>
        </Button>
      </div>
    </div>
  );
};

const RequestPasswordResetComponent = ({
  setShowRequestPasswordReset,
}: {
  setShowRequestPasswordReset: (showRequestPasswordReset: boolean) => void;
}) => {
  const form = useForm<RequestResetPassword>({
    resolver: zodResolver(requestResetPasswordSchema),
    defaultValues: {
      emailAddress: '',
    },
  });

  const [submittedReset, setSubmittedReset] = useState<boolean>(false);

  const onSubmit = async (requestResetPassword: RequestResetPassword) => {
    try {
      await maybeAddTenantIdToAuth(requestResetPassword.emailAddress);
    } catch {
      return form.setError('root', {
        type: 'manual',
        message: 'Gebruiker heeft geen tenantId.',
      });
    }

    setSubmittedReset(false);

    try {
      await sendPasswordResetEmail(auth, requestResetPassword.emailAddress, {
        url: getFirebaseContinueUrl(),
      });
      setSubmittedReset(true);
      form.setValue('emailAddress', '');
    } catch (error) {
      setSubmittedReset(false);
      if (error instanceof Error && 'code' in error && error.code === 'auth/user-not-found') {
        form.setError('emailAddress', {
          type: 'manual',
          message: 'Geen account gevonden voor dit e-mailadres.',
        });
      } else {
        form.setError('root', {
          type: 'manual',
          message: 'Er is iets misgegaan. Probeer het opnieuw.',
        });
      }
    }
  };

  const onBackClick = () => {
    setShowRequestPasswordReset(false);
    setSubmittedReset(false);
  };

  return (
    <div className="flex min-h-screen flex-row items-center justify-center">
      <div className="flex w-[317px] flex-col items-start justify-center gap-6">
        <Logo className="size-10" strokeWidth="1.5" />
        <Heading3>Wachtwoord resetten</Heading3>
        {submittedReset && (
          <div className="flex items-start gap-2 rounded-xl border border-border bg-background p-4">
            <Footnote className="text-foreground">
              Als er een account gekoppeld is aan dit e-mailadres, hebben we je een e-mail gestuurd
              met een link om je wachtwoord te resetten.
            </Footnote>
          </div>
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col items-start gap-6"
          >
            <FormFieldText
              control={form.control}
              name="emailAddress"
              label="Email"
              description="We sturen een e-mail met instructies om je wachtwoord opnieuw in te stellen."
              inputStyle="rounded-input border border-input bg-background py-2 pr-3 pl-4 w-full"
            />

            <FormMessageRoot />

            <div className="flex w-full flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
              <Button className="w-full px-3 py-2" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="animate-spin" />}
                Wachtwoord resetten
              </Button>
            </div>
          </form>
        </Form>
        <Button variant="link" className="border-none p-0 shadow-none" onClick={onBackClick}>
          <Footnote className="text-muted-foreground underline">Terug</Footnote>
        </Button>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/(auth)/login')({
  component: LoginPage,
  validateSearch: (search) => searchSchema.parse(search),
});

function LoginPage() {
  const [showRequestPasswordReset, setShowRequestPasswordReset] = useState<boolean>(false);

  return showRequestPasswordReset ? (
    <RequestPasswordResetComponent setShowRequestPasswordReset={setShowRequestPasswordReset} />
  ) : (
    <LoginComponent setShowRequestPasswordReset={setShowRequestPasswordReset} />
  );
}
